import React, { useEffect } from "react";

import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import {
  QUERY_CLIENT,
  QUERY_CLIENT_PREFERENCES,
} from "../../../../config/graphql/query";
import { UPDATE_CLIENT_PREFERENCES } from "../../../../config/graphql/mutation";

import { useClientId } from "../hooks/useClient";
import ChatLanguagesSetting, {
  DEFAULT_CHAT_LANGUAGES,
} from "./ChatLanguagesSetting";

type FieldValues = {
  preferences: {
    chat: IChatPreferences;
  };
};

/**
 * The languages the Teams / Google Chat arrival message is written in — one setting for
 * both channels, which is why it is not on either integration tab.
 */
const ChatLanguages = React.memo(() => {
  const { t } = useTranslation(["chatLanguages"]);

  const clientId = useClientId();

  const { data } = useQuery<{
    client: { preferences: IClientPreferences };
  }>(QUERY_CLIENT_PREFERENCES, {
    skip: !clientId,
    variables: { id: clientId },
    context: { headers: { ClientId: clientId } },
    fetchPolicy: "network-only",
  });

  const [onUpdate] = useMutation(UPDATE_CLIENT_PREFERENCES, {
    context: { headers: { ClientId: clientId } },
    refetchQueries: [
      { query: QUERY_CLIENT, variables: { id: clientId } },
      {
        query: QUERY_CLIENT_PREFERENCES,
        variables: { id: clientId },
        context: { headers: { ClientId: clientId } },
      },
    ],
  });

  const methods = useForm<FieldValues>({ shouldFocusError: false });

  useEffect(() => {
    if (data?.client) {
      const languages = data.client.preferences?.chat?.languages;

      methods.reset({
        preferences: {
          chat: {
            // What the server sends for an unset list, so the form starts from the truth.
            languages: languages?.length ? languages : DEFAULT_CHAT_LANGUAGES,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const onSubmit = async (variables: FieldValues) => {
    try {
      await onUpdate({
        variables: {
          input: {
            id: clientId,
            preferences: {
              chat: {
                languages: variables.preferences?.chat?.languages || [],
              },
            },
          },
        },
      });
      toast.success<string>(t("chatLanguages:toast.saved"));
    } catch (e: any) {
      toast.error<string>(e.message || t("chatLanguages:toast.saveError"));
    }
  };

  return (
    <FormProvider {...methods}>
      <form className="mb-5" onSubmit={methods.handleSubmit(onSubmit)}>
        <ChatLanguagesSetting />
        <button
          type="submit"
          className="btn btn-primary"
          disabled={methods.formState.isSubmitting}
        >
          {t("chatLanguages:button.submit")}
        </button>
      </form>
    </FormProvider>
  );
});

export default ChatLanguages;
